import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

type Source = 'pg' | 'ddb';

interface TableList {
  postgres: string[];
  dynamodb: string[];
}

const token = () =>
  localStorage.getItem('intrlock_token')
  || localStorage.getItem('jwt')
  || localStorage.getItem('token')
  || '';

const fetchJSON = async (path: string) => {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => '')}`);
  return res.json();
};

export const AdminDataBrowser: React.FC = () => {
  const [tables, setTables] = useState<TableList | null>(null);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState<{ source: Source; table: string } | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [entityFilter, setEntityFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchJSON('/admin/db/tables')
      .then(setTables)
      .catch((e) => setError(e.message));
  }, []);

  const loadRows = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        source: active.source,
        table: active.table,
        limit: String(limit),
      });
      if (active.source === 'ddb' && entityFilter) params.set('entityType', entityFilter);
      const data = await fetchJSON(`/admin/db/rows?${params.toString()}`);
      setRows(data.rows || []);
    } catch (e: any) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [active, limit, entityFilter]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const filteredRows = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return JSON.stringify(r).toLowerCase().includes(s);
  });

  const columns = filteredRows.length
    ? Array.from(new Set(filteredRows.flatMap((r) => Object.keys(r))))
    : [];

  const cellRender = (val: any) => {
    if (val === null || val === undefined) return <span className="text-gray-400">—</span>;
    if (typeof val === 'boolean') return val ? '✓' : '✗';
    if (typeof val === 'object') return <code className="text-xs">{JSON.stringify(val)}</code>;
    const s = String(val);
    return s.length > 80 ? <span title={s}>{s.slice(0, 80)}…</span> : s;
  };

  const filterTables = (list: string[]) =>
    list.filter((t) => !filter || t.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Data Browser</h1>
      <p className="text-sm text-gray-500 mb-6">
        Read-only view of every Postgres + DynamoDB table powering the platform. Super-admin only.
      </p>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="col-span-3">
          <input
            type="text"
            placeholder="Filter tables…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full mb-3 border border-gray-300 rounded px-3 py-2 text-sm"
          />
          {tables ? (
            <>
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                  PostgreSQL ({tables.postgres.length})
                </h3>
                <div className="space-y-1">
                  {filterTables(tables.postgres).map((t) => (
                    <button
                      key={`pg-${t}`}
                      onClick={() => { setActive({ source: 'pg', table: t }); setEntityFilter(''); setSearch(''); }}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm font-mono transition-colors ${
                        active?.source === 'pg' && active.table === t
                          ? 'bg-emerald-100 text-emerald-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                  DynamoDB ({tables.dynamodb.length})
                </h3>
                <div className="space-y-1">
                  {filterTables(tables.dynamodb).map((t) => (
                    <button
                      key={`ddb-${t}`}
                      onClick={() => { setActive({ source: 'ddb', table: t }); setEntityFilter(''); setSearch(''); }}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm font-mono transition-colors ${
                        active?.source === 'ddb' && active.table === t
                          ? 'bg-emerald-100 text-emerald-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >{t}</button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Loading tables…</p>
          )}
        </aside>

        {/* Main */}
        <main className="col-span-9">
          {!active ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center text-gray-500">
              Select a table from the left to view its rows.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="font-mono text-lg font-semibold text-gray-900">
                  {active.source.toUpperCase()} · {active.table}
                </h2>
                <span className="text-sm text-gray-500">
                  {loading ? 'Loading…' : `${filteredRows.length} of ${rows.length} rows`}
                </span>
                <button
                  onClick={loadRows}
                  className="ml-auto px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  Refresh
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4 text-sm">
                <input
                  type="text"
                  placeholder="Search loaded rows…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                />
                {active.source === 'ddb' && (
                  <input
                    type="text"
                    placeholder="entityType filter (optional)"
                    value={entityFilter}
                    onChange={(e) => setEntityFilter(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-56 font-mono text-xs"
                  />
                )}
                <label className="text-xs text-gray-500">Limit</label>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                </select>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {filteredRows.length === 0 ? (
                <p className="text-gray-500 text-sm">No rows.</p>
              ) : (
                <div className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: '70vh' }}>
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {columns.map((c) => (
                          <th
                            key={c}
                            className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
                          >{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50 border-b border-gray-100">
                          {columns.map((c) => (
                            <td key={c} className="px-3 py-2 align-top max-w-md">
                              {cellRender(r[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};
